import React from 'react';

interface MessageContentRendererProps {
  content: string;
  className?: string;
}

const MessageContentRenderer: React.FC<MessageContentRendererProps> = ({ content, className = "" }) => {
  // Regular expression to detect image URLs in markdown link format: [text](url)
  const imageUrlRegex = /\[([^\]]*)\]\((https?:\/\/[^\s)]+\.(?:png|jpg|jpeg|gif|webp|svg))\)/gi;
  
  // Regular expression to detect direct image URLs
  const directImageUrlRegex = /(https?:\/\/[^\s]+\.(?:png|jpg|jpeg|gif|webp|svg))/gi;
  
  // Regular expression to detect markdown-style links: [text](url) - but not images
  const markdownLinkRegex = /\[([^\]]*)\]\(((?:https?:\/\/|www\.)[^\s)]+(?<!\.(?:png|jpg|jpeg|gif|webp|svg)))\)/gi;
  
  // Regular expression to detect direct URLs (with http/https) - but not images
  const directUrlRegex = /(https?:\/\/[^\s,;!?]+(?<!\.(?:png|jpg|jpeg|gif|webp|svg)))/gi;
  
  // Regular expression to detect www URLs without protocol - but not images
  const wwwUrlRegex = /(www\.[^\s,;!?]+(?<!\.(?:png|jpg|jpeg|gif|webp|svg)))/gi;
  
  // Function to render content with inline images and clickable links
  const renderContentWithImagesAndLinks = (text: string) => {
    const parts: (string | React.ReactElement)[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let elementKey = 0;

    // First, handle markdown-style image links [text](url)
    const markdownImageMatches: Array<{ match: RegExpExecArray; type: 'markdownImage' }> = [];
    while ((match = imageUrlRegex.exec(text)) !== null) {
      markdownImageMatches.push({ match, type: 'markdownImage' });
    }
    imageUrlRegex.lastIndex = 0;

    // Handle markdown-style links [text](url) - but not images
    const markdownLinkMatches: Array<{ match: RegExpExecArray; type: 'markdownLink' }> = [];
    while ((match = markdownLinkRegex.exec(text)) !== null) {
      // Check if this is already part of an image markdown
      const isInImageMarkdown = markdownImageMatches.some(imgMatch => 
        match!.index >= imgMatch.match.index && 
        match!.index < imgMatch.match.index + imgMatch.match[0].length
      );
      
      if (!isInImageMarkdown) {
        markdownLinkMatches.push({ match, type: 'markdownLink' });
      }
    }
    markdownLinkRegex.lastIndex = 0;

    // Then handle direct image URLs (but skip those already in markdown)
    const directImageMatches: Array<{ match: RegExpExecArray; type: 'directImage' }> = [];
    while ((match = directImageUrlRegex.exec(text)) !== null) {
      // Check if this URL is already part of any markdown
      const isInMarkdown = [...markdownImageMatches, ...markdownLinkMatches].some(mdMatch => 
        match!.index >= mdMatch.match.index && 
        match!.index < mdMatch.match.index + mdMatch.match[0].length
      );
      
      if (!isInMarkdown) {
        directImageMatches.push({ match, type: 'directImage' });
      }
    }
    directImageUrlRegex.lastIndex = 0;

    // Handle direct URLs (but skip those already in markdown or images)
    const directUrlMatches: Array<{ match: RegExpExecArray; type: 'directUrl' }> = [];
    while ((match = directUrlRegex.exec(text)) !== null) {
      // Check if this URL is already part of any other match
      const isAlreadyMatched = [...markdownImageMatches, ...markdownLinkMatches, ...directImageMatches].some(otherMatch => 
        match!.index >= otherMatch.match.index && 
        match!.index < otherMatch.match.index + otherMatch.match[0].length
      );
      
      if (!isAlreadyMatched) {
        directUrlMatches.push({ match, type: 'directUrl' });
      }
    }
    directUrlRegex.lastIndex = 0;

    // Handle www URLs without protocol (but skip those already in other matches)
    const wwwUrlMatches: Array<{ match: RegExpExecArray; type: 'wwwUrl' }> = [];
    while ((match = wwwUrlRegex.exec(text)) !== null) {
      // Check if this URL is already part of any other match
      const isAlreadyMatched = [...markdownImageMatches, ...markdownLinkMatches, ...directImageMatches, ...directUrlMatches].some(otherMatch => 
        match!.index >= otherMatch.match.index && 
        match!.index < otherMatch.match.index + otherMatch.match[0].length
      );
      
      if (!isAlreadyMatched) {
        wwwUrlMatches.push({ match, type: 'wwwUrl' });
      }
    }
    wwwUrlRegex.lastIndex = 0;

    // Combine and sort all matches by position
    const allMatches = [...markdownImageMatches, ...markdownLinkMatches, ...directImageMatches, ...directUrlMatches, ...wwwUrlMatches]
      .sort((a, b) => a.match.index - b.match.index);

    // Process all matches in order
    allMatches.forEach(({ match, type }) => {
      // Add text before the match
      if (match.index > lastIndex) {
        const textBefore = text.slice(lastIndex, match.index);
        if (textBefore) {
          parts.push(textBefore);
        }
      }

      if (type === 'markdownImage') {
        // Render markdown-style image: [alt text](url)
        const altText = match[1] || 'AI Image';
        const imageUrl = match[2];
        
        parts.push(
          <div key={`img-${elementKey++}`} className="my-2">
            <img
              src={imageUrl}
              alt={altText}
              className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm"
              style={{ maxHeight: '300px' }}
              onError={(e) => {
                console.error('Image load error:', e);
                // Replace with a placeholder on error
                e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="100"><rect width="200" height="100" fill="%23f3f4f6"/><text x="100" y="50" text-anchor="middle" dy=".3em" fill="%236b7280" font-size="12">Image not available</text></svg>';
              }}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{altText}</p>
          </div>
        );
      } else if (type === 'directImage') {
        // Render direct image URL
        const imageUrl = match[0];
        
        parts.push(
          <div key={`img-${elementKey++}`} className="my-2">
            <img
              src={imageUrl}
              alt="AI Image"
              className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm"
              style={{ maxHeight: '300px' }}
              onError={(e) => {
                console.error('Image load error:', e);
                e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="100"><rect width="200" height="100" fill="%23f3f4f6"/><text x="100" y="50" text-anchor="middle" dy=".3em" fill="%236b7280" font-size="12">Image not available</text></svg>';
              }}
            />
          </div>
        );
      } else if (type === 'markdownLink') {
        // Render markdown-style link: [text](url)
        const linkText = match[1] || match[2]; // Use link text or fallback to URL
        let linkUrl = match[2];
        
        // Add https:// protocol if it's a www URL without protocol
        if (linkUrl.startsWith('www.') && !linkUrl.startsWith('http')) {
          linkUrl = `https://${linkUrl}`;
        }
        
        parts.push(
          <a
            key={`link-${elementKey++}`}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all duration-200"
            onClick={(e) => {
              // Ensure the link opens in a new tab
              e.preventDefault();
              window.open(linkUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            {linkText}
          </a>
        );
      } else if (type === 'directUrl') {
        // Render direct URL as clickable link
        const linkUrl = match[0];
        
        // Create a shortened display text for long URLs
        const displayText = linkUrl.length > 50 ? `${linkUrl.substring(0, 47)}...` : linkUrl;
        
        parts.push(
          <a
            key={`link-${elementKey++}`}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all duration-200"
            onClick={(e) => {
              // Ensure the link opens in a new tab
              e.preventDefault();
              window.open(linkUrl, '_blank', 'noopener,noreferrer');
            }}
            title={linkUrl} // Show full URL on hover
          >
            {displayText}
          </a>
        );
      } else if (type === 'wwwUrl') {
        // Render www URL as clickable link (add https:// protocol)
        const originalUrl = match[0];
        const linkUrl = `https://${originalUrl}`;
        
        // Create a shortened display text for long URLs
        const displayText = originalUrl.length > 50 ? `${originalUrl.substring(0, 47)}...` : originalUrl;
        
        parts.push(
          <a
            key={`link-${elementKey++}`}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline decoration-1 underline-offset-2 hover:decoration-2 transition-all duration-200"
            onClick={(e) => {
              // Ensure the link opens in a new tab
              e.preventDefault();
              window.open(linkUrl, '_blank', 'noopener,noreferrer');
            }}
            title={linkUrl} // Show full URL with protocol on hover
          >
            {displayText}
          </a>
        );
      }

      lastIndex = match.index + match[0].length;
    });

    // Add remaining text after the last match
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        parts.push(remainingText);
      }
    }

    // If no matches found, return the original text
    if (parts.length === 0) {
      parts.push(text);
    }

    return parts;
  };

  const contentParts = renderContentWithImagesAndLinks(content);

  return (
    <div className={className}>
      {contentParts.map((part, index) => {
        if (typeof part === 'string') {
          // Render text parts with proper line breaks
          return (
            <span key={`text-${index}`} className="whitespace-pre-wrap break-words">
              {part}
            </span>
          );
        } else {
          // Render image elements
          return part;
        }
      })}
    </div>
  );
};

export default MessageContentRenderer;
